import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Plus, Trash2, Pencil, Search, FileText, Loader2, Check, Copy, Image as ImageIcon, EyeOff, CheckCircle2, AlertTriangle, Languages, ClipboardCopy, Archive, ArchiveRestore, RotateCcw } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { ean13CheckDigit } from "../lib/ean13";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useConfirm } from "../components/ConfirmDialog";
import NutritionImporter from "../components/NutritionImporter";
import PriceMarginCell from "../components/PriceMarginCell";
import ProductShopBlock from "../components/ProductShopBlock";
import ProductPackagingEditor from "../components/products/ProductPackagingEditor";
import { NumericInput } from "../components/ui/numeric-input";
import { toast } from "sonner";

const ALLERGENS = [
  "Gluten", "Œufs", "Lait", "Poisson", "Crustacés", "Arachides", "Fruits à coque",
  "Soja", "Céleri", "Moutarde", "Sésame", "Sulfites", "Lupin", "Mollusques",
];

const NUTRITION_FIELDS = [
  { key: "energy_kj", label: "Énergie (kJ)", unit: "kJ" },
  { key: "energy_kcal", label: "Énergie (kcal)", unit: "kcal" },
  { key: "fat", label: "Matières grasses", unit: "g" },
  { key: "saturated_fat", label: "  dont acides gras saturés", unit: "g" },
  { key: "carbs", label: "Glucides", unit: "g" },
  { key: "sugars", label: "  dont sucres", unit: "g" },
  { key: "proteins", label: "Protéines", unit: "g" },
  { key: "salt", label: "Sel", unit: "g" },
];

const emptyNutrition = { energy_kj: "", energy_kcal: "", fat: "", saturated_fat: "", carbs: "", sugars: "", proteins: "", salt: "" };

const emptyForm = { sku: "", name: "", category: "Général", unit: "kg", allergens: [], ingredients: "", nutrition: { ...emptyNutrition }, storage_instructions: "", reheating_instructions: "", net_weight: "", net_weight_unit: "g", net_weight_detail: "", ean13_base: "", min_stock: 0, shelf_life_days: "", description: "", price_direct: "", price_distributor: "", price_online: "", price_traiteur: "", cost_price: "", photo_url: "", photo_packaging_url: "", online_visible: true, shop_sold_out: false, vending_only: false, sellable_vending: true, stock_managed: true, short_description: "", spice_level: "", packaging: [], nayax_aliases: [], nayax_product_id: "" };

export default function Products() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [catalogFilter, setCatalogFilter] = useState("all"); // all | in | out | nophoto | hidden
  const [sortBy, setSortBy] = useState("default"); // default | sku-asc | sku-desc | name-asc | name-desc | recent
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | dirty | saving | saved
  const [costBreakdown, setCostBreakdown] = useState(null); // Phase 32b — coût recette dans la modal
  const [minMarginPct, setMinMarginPct] = useState(30); // Phase 33c — seuil d'alerte marge
  const [eanSuggestion, setEanSuggestion] = useState(null); // {configured, next_full, used_count, capacity, hint}
  const formChangedSinceLoad = useRef(false);
  const saveTimer = useRef(null);
  const confirm = useConfirm();
  // Phase 87bf — Filtre archivés (bool historique — conservé pour compat backend)
  // Phase 101di (Feb 2026) — Enrichi : 3 modes visuels au lieu d'un checkbox binaire
  //   • "active"   : affiche uniquement les produits actifs (défaut)
  //   • "all"      : affiche tout (actifs + archivés)
  //   • "inactive" : affiche uniquement les archivés (utile pour retrouver un
  //                  produit à réactiver sans scroller toute la liste)
  const [archiveFilter, setArchiveFilter] = useState("active");
  const includeArchived = archiveFilter !== "active"; // dérivé : requête backend inclut archivés si tous/inactifs
  // Phase 87bf — Validation live SKU/Nom dans le dialog produit
  const [skuCheck, setSkuCheck] = useState({ status: "idle", conflict: null });
  const [nameCheck, setNameCheck] = useState({ status: "idle", conflict: null });
  const skuCheckTimer = useRef(null);
  const nameCheckTimer = useRef(null);

  const load = async () => {
    const url = includeArchived ? "/products?include_archived=true" : "/products";
    const [{ data: p }, { data: c }, settingsRes, eanRes] = await Promise.all([
      api.get(url),
      api.get("/product-categories").catch(() => ({ data: [] })),
      api.get("/settings").catch(() => ({ data: { min_margin_percent: 30 } })),
      api.get("/products/ean13-next-suggestion").catch(() => ({ data: null })),
    ]);
    setProducts(p);
    setCategories(c);
    setMinMarginPct(Number(settingsRes.data?.min_margin_percent ?? 30));
    setEanSuggestion(eanRes.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [includeArchived]);

  // Phase 87bf — Live check SKU (debounced 350ms). État `idle / checking / ok / conflict`
  useEffect(() => {
    if (skuCheckTimer.current) clearTimeout(skuCheckTimer.current);
    const sku = (form.sku || "").trim();
    if (!sku) { setSkuCheck({ status: "idle", conflict: null }); return; }
    setSkuCheck({ status: "checking", conflict: null });
    skuCheckTimer.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ sku });
        if (editing?.id) params.set("exclude", editing.id);
        const { data } = await api.get(`/products/check-sku?${params}`);
        setSkuCheck({ status: data.available ? "ok" : "conflict", conflict: data.conflict });
      } catch {
        setSkuCheck({ status: "idle", conflict: null });
      }
    }, 350);
    return () => skuCheckTimer.current && clearTimeout(skuCheckTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.sku, editing?.id]);

  // Phase 87bf — Live check NOM (debounced 350ms)
  useEffect(() => {
    if (nameCheckTimer.current) clearTimeout(nameCheckTimer.current);
    const name = (form.name || "").trim();
    if (!name) { setNameCheck({ status: "idle", conflict: null }); return; }
    setNameCheck({ status: "checking", conflict: null });
    nameCheckTimer.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ name });
        if (editing?.id) params.set("exclude", editing.id);
        const { data } = await api.get(`/products/check-name?${params}`);
        setNameCheck({ status: data.available ? "ok" : "conflict", conflict: data.conflict });
      } catch {
        setNameCheck({ status: "idle", conflict: null });
      }
    }, 350);
    return () => nameCheckTimer.current && clearTimeout(nameCheckTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name, editing?.id]);

  const openCreate = async () => {
    setEditing(null);
    setForm(emptyForm);
    setSaveStatus("idle");
    formChangedSinceLoad.current = false;
    setCostBreakdown(null);
    setOpen(true);
    // Charge la suggestion EAN13 (silencieux si non configuré)
    try {
      const { data } = await api.get("/products/ean13-next-suggestion");
      setEanSuggestion(data);
    } catch {
      setEanSuggestion(null);
    }
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      sku: p.sku,
      name: p.name,
      category: p.category,
      unit: p.unit,
      allergens: p.allergens || [],
      ingredients: p.ingredients || "",
      nutrition: { ...emptyNutrition, ...(p.nutrition || {}) },
      storage_instructions: p.storage_instructions || "",
      reheating_instructions: p.reheating_instructions || "",
      net_weight: p.net_weight ?? "",
      net_weight_unit: p.net_weight_unit || "g",
      net_weight_detail: p.net_weight_detail || "",
      ean13_base: p.ean13_base || "",
      min_stock: p.min_stock,
      shelf_life_days: p.shelf_life_days ?? "",
      description: p.description || "",
      price_direct: p.price_direct ?? "",
      price_distributor: p.price_distributor ?? "",
      price_online: p.price_online ?? "",
      price_traiteur: p.price_traiteur ?? "",
      cost_price: p.cost_price ?? "",
      photo_url: p.photo_url || "",
      photo_packaging_url: p.photo_packaging_url || "",
      online_visible: p.online_visible !== false,
      shop_sold_out: !!p.shop_sold_out,
      vending_only: !!p.vending_only,
      sellable_vending: p.sellable_vending !== false,  // Phase 101ep — défaut true si non défini (rétrocompat)
      stock_managed: p.stock_managed !== false,  // Phase 87bc — défaut true (HACCP) ; false = boisson stock simple
      short_description: p.short_description || "",
      spice_level: p.spice_level ?? "",
      packaging: p.packaging || [],
      nayax_aliases: p.nayax_aliases || [],
      nayax_product_id: p.nayax_product_id != null ? String(p.nayax_product_id) : "",
    });
    setSaveStatus("idle");
    formChangedSinceLoad.current = false;
    // Phase 32b — coût calculé depuis la recette (fiche technique)
    setCostBreakdown(null);
    api.get(`/products/${p.id}/cost-breakdown`)
      .then(({ data }) => setCostBreakdown(data))
      .catch(() => setCostBreakdown(null));
    // Phase 87aj — Rafraîchit la suggestion EAN13 pour les produits sans code
    api.get("/products/ean13-next-suggestion")
      .then(({ data }) => setEanSuggestion(data))
      .catch(() => {});
    setOpen(true);
  };

  const toggleAllergen = (a) => {
    setForm((f) => ({ ...f, allergens: f.allergens.includes(a) ? f.allergens.filter((x) => x !== a) : [...f.allergens, a] }));
  };

  const parseNumberInput = (v) => {
    // Accepte virgule décimale CH/FR : "8,50" → 8.5
    if (v === "" || v === null || v === undefined) return null;
    const cleaned = String(v).replace(",", ".").trim();
    const n = Number(cleaned);
    return isNaN(n) ? null : n;
  };

  const buildPayload = () => {
    const nut = {};
    let hasAny = false;
    for (const k of Object.keys(emptyNutrition)) {
      const v = form.nutrition?.[k];
      if (v === "" || v === null || v === undefined) {
        nut[k] = null;
      } else {
        const n = parseNumberInput(v);
        nut[k] = n;
        if (n !== null) hasAny = true;
      }
    }
    return {
      ...form,
      _aliasDraft: undefined,  // Phase 87aw — Champ UI temporaire, ne pas envoyer
      nayax_aliases: (form.nayax_aliases || []).map((a) => String(a).trim()).filter(Boolean),
      nayax_product_id: form.nayax_product_id ? parseInt(form.nayax_product_id, 10) : null,
      min_stock: parseNumberInput(form.min_stock) ?? 0,
      shelf_life_days: parseNumberInput(form.shelf_life_days),
      net_weight: parseNumberInput(form.net_weight),
      ean13_base: (form.ean13_base || "").replace(/\D/g, "").slice(0, 12),
      nutrition: hasAny ? nut : null,
      price_direct: parseNumberInput(form.price_direct),
      price_distributor: parseNumberInput(form.price_distributor),
      price_online: parseNumberInput(form.price_online),
      price_traiteur: parseNumberInput(form.price_traiteur),
      cost_price: parseNumberInput(form.cost_price),
      photo_url: (form.photo_url || "").trim() || null,
      photo_packaging_url: (form.photo_packaging_url || "").trim() || null,
      online_visible: !!form.online_visible,
      shop_sold_out: !!form.shop_sold_out,
      vending_only: !!form.vending_only,
      sellable_vending: form.sellable_vending !== false,   // Phase 101ep
      short_description: (form.short_description || "").trim(),
      spice_level: form.spice_level === "" || form.spice_level === null ? null : Number(form.spice_level),
    };
  };

  // Auto-save (uniquement en mode édition, après modification, avec validation minimale)
  useEffect(() => {
    if (!open || !editing) return;
    if (!formChangedSinceLoad.current) {
      formChangedSinceLoad.current = true;
      return; // ignorer le 1er render après ouverture
    }
    if (!form.sku || !form.name) {
      setSaveStatus("dirty");
      return;
    }
    setSaveStatus("dirty");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaveStatus("saving");
      try {
        await api.put(`/products/${editing.id}`, buildPayload());
        setSaveStatus("saved");
        await load();
      } catch (err) {
        setSaveStatus("dirty");
        // Affiche la vraie raison (FastAPI / Pydantic)
        const detail = err?.response?.data?.detail;
        let msg = "Échec de l'enregistrement automatique";
        if (typeof detail === "string") {
          msg = `Échec auto : ${detail}`;
        } else if (Array.isArray(detail) && detail[0]) {
          const e0 = detail[0];
          const field = (e0.loc || []).slice(1).join(".") || "champ";
          msg = `Échec auto · ${field} : ${e0.msg || "valeur invalide"}`;
        }
        toast.error(msg);
      }
    }, 1200);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, open, editing]);

  const save = async () => {
    if (!form.sku || !form.name) {
      toast.error("SKU et nom sont obligatoires");
      return;
    }
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, buildPayload());
        toast.success("Produit mis à jour");
      } else {
        await api.post("/products", buildPayload());
        toast.success("Produit créé");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  // Phase 87bf — Soft-delete : on n'efface plus rien, on "désactive" (archived=true)
  // Lots / mouvements / factures historiques RESTENT consultables (HACCP + audit).
  const remove = async (p) => {
    const ok = await confirm({
      title: `Désactiver « ${p.name} » ?`,
      description: "Le produit sera masqué des listes (catalogue, boutique, carte) mais ses lots, mouvements, fiches techniques et factures historiques resteront intacts pour la traçabilité HACCP et l'audit. Réversible via le bouton « Réactiver ».",
      confirmText: "Désactiver",
    });
    if (!ok) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success(`« ${p.name} » désactivé`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  // Phase 87bf — Réactivation d'un produit archivé
  const reactivate = async (p) => {
    try {
      await api.put(`/products/${p.id}`, { archived: false });
      toast.success(`« ${p.name} » réactivé`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const filtered = products.filter((p) => {
    const q = query.toLowerCase();
    const matchesText = !q || p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q) || p.category.toLowerCase().includes(q);
    if (!matchesText) return false;
    // Phase 101di — filtre archive tri-state (client-side)
    if (archiveFilter === "inactive" && !p.archived) return false;
    // "active" est déjà filtré côté backend (include_archived=false)
    // "all" laisse tout passer
    if (catalogFilter === "all") return true;
    const hasPhoto = !!(p.photo_url && String(p.photo_url).trim());
    const hasPackaging = !!(p.photo_packaging_url && String(p.photo_packaging_url).trim());
    const isVisible = p.online_visible !== false;
    const inCatalog = isVisible && hasPhoto;
    if (catalogFilter === "in") return inCatalog;
    if (catalogFilter === "out") return !inCatalog;
    if (catalogFilter === "nophoto") return isVisible && !hasPhoto;
    if (catalogFilter === "nopackaging") return inCatalog && !hasPackaging;
    if (catalogFilter === "hidden") return !isVisible;
    if (catalogFilter === "noean") return !(p.ean13_base && p.ean13_base.trim().length === 12);
    return true;
  });

  // Tri : SKU / nom / date. Naturel pour les SKU type "SKU-P12" qui mélangent texte et chiffres.
  const sorted = (() => {
    if (sortBy === "default") return filtered;
    const arr = [...filtered];
    const naturalCmp = (a, b) => String(a || "").localeCompare(String(b || ""), "fr", { numeric: true, sensitivity: "base" });
    if (sortBy === "sku-asc")  arr.sort((a, b) => naturalCmp(a.sku, b.sku));
    if (sortBy === "sku-desc") arr.sort((a, b) => naturalCmp(b.sku, a.sku));
    if (sortBy === "name-asc")  arr.sort((a, b) => naturalCmp(a.name, b.name));
    if (sortBy === "name-desc") arr.sort((a, b) => naturalCmp(b.name, a.name));
    if (sortBy === "recent")   arr.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
    // Phase 87an — Tri par catégorie (asc puis par nom dans chaque catégorie)
    if (sortBy === "category-asc") arr.sort((a, b) => {
      const c = naturalCmp(a.category || "Général", b.category || "Général");
      return c !== 0 ? c : naturalCmp(a.name, b.name);
    });
    if (sortBy === "category-desc") arr.sort((a, b) => {
      const c = naturalCmp(b.category || "Général", a.category || "Général");
      return c !== 0 ? c : naturalCmp(a.name, b.name);
    });
    return arr;
  })();

  // Détecteur de doublons EAN13 (P2) : groupe les produits partageant le même
  // ean13_base. Affiche un ⚠ rouge devant le SKU des produits en conflit, avec
  // tooltip listant les autres SKU concernés. Empêche les erreurs de validation
  // backend (ean13_base unique côté DB) avant édition.
  const eanDuplicateMap = (() => {
    const buckets = new Map();
    for (const p of products) {
      const base = (p.ean13_base || "").trim();
      if (!base || base.length < 12) continue;
      if (!buckets.has(base)) buckets.set(base, []);
      buckets.get(base).push({ id: p.id, sku: p.sku, name: p.name });
    }
    const dupByProductId = new Map();
    for (const [base, items] of buckets.entries()) {
      if (items.length <= 1) continue;
      for (const it of items) {
        const others = items.filter((o) => o.id !== it.id);
        dupByProductId.set(it.id, { base, others });
      }
    }
    return dupByProductId;
  })();

  const counts = (() => {
    let inC = 0, complete = 0, nopackaging = 0, nophoto = 0, hidden = 0, noean = 0;
    for (const p of products) {
      const hasPhoto = !!(p.photo_url && String(p.photo_url).trim());
      const hasPackaging = !!(p.photo_packaging_url && String(p.photo_packaging_url).trim());
      const isVisible = p.online_visible !== false;
      const hasEan = !!(p.ean13_base && p.ean13_base.trim().length === 12);
      if (!hasEan) noean++;
      if (!isVisible) hidden++;
      else if (!hasPhoto) nophoto++;
      else {
        inC++;
        if (hasPackaging) complete++;
        else nopackaging++;
      }
    }
    return { inC, complete, nopackaging, nophoto, hidden, noean, out: nophoto + hidden, total: products.length };
  })();

  return (
    <div className="space-y-6" data-testid="products-page">
      {/* Phase 84 — Widget "Prochain EAN13 disponible" pour préparer Nayax/Access en parallèle */}
      {eanSuggestion?.configured && eanSuggestion.next_full && (
        <div
          className="bg-gradient-to-r from-[#FBF4E9] to-[#F5ECDA] border border-[#E8D3A8] rounded-md p-3 flex items-center gap-4 flex-wrap"
          data-testid="ean13-next-widget"
        >
          <div className="text-[10px] uppercase tracking-widest text-[#8C6B2F] font-semibold shrink-0">
            Prochain EAN13 disponible
          </div>
          <div className="flex items-baseline gap-2 font-mono flex-1 min-w-[230px]">
            <span className="text-lg font-bold text-[#1F4E3D]" data-testid="ean13-next-base">
              {eanSuggestion.next_full}
            </span>
            <span className="text-xl font-bold text-[#8C6B2F]" data-testid="ean13-next-checkdigit">
              {ean13CheckDigit(eanSuggestion.next_full)}
            </span>
            <span className="text-[10px] text-[#575A57] italic">
              ({eanSuggestion.used_count} / {eanSuggestion.capacity} utilisés)
            </span>
          </div>
          <button
            type="button"
            onClick={async () => {
              const full13 = eanSuggestion.next_full + ean13CheckDigit(eanSuggestion.next_full);
              try {
                await navigator.clipboard.writeText(full13);
                toast.success(`EAN13 copié : ${full13}`, { description: "Vous pouvez maintenant le coller dans Nayax." });
              } catch {
                toast.error("Impossible de copier — sélectionnez et copiez manuellement.");
              }
            }}
            data-testid="ean13-copy-btn"
            className="text-xs px-3 py-1.5 bg-[#1F4E3D] text-white rounded hover:bg-[#173B2E] flex items-center gap-1.5 shrink-0"
            title="Copie les 13 chiffres complets (avec checksum) dans le presse-papier"
          >
            <ClipboardCopy className="w-3.5 h-3.5" /> Copier les 13 chiffres
          </button>
        </div>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#575A57]" />
          <Input
            placeholder="Rechercher par nom, SKU, catégorie…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 bg-white"
            data-testid="product-search"
          />
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[210px] bg-white" data-testid="product-sort">
            <SelectValue placeholder="Trier par…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default" data-testid="sort-default">Catégorie + Nom (défaut)</SelectItem>
            <SelectItem value="category-asc" data-testid="sort-category-asc">Catégorie croissante (A → Z)</SelectItem>
            <SelectItem value="category-desc" data-testid="sort-category-desc">Catégorie décroissante (Z → A)</SelectItem>
            <SelectItem value="sku-asc" data-testid="sort-sku-asc">SKU croissant (A → Z)</SelectItem>
            <SelectItem value="sku-desc" data-testid="sort-sku-desc">SKU décroissant (Z → A)</SelectItem>
            <SelectItem value="name-asc" data-testid="sort-name-asc">Nom croissant (A → Z)</SelectItem>
            <SelectItem value="name-desc" data-testid="sort-name-desc">Nom décroissant (Z → A)</SelectItem>
            <SelectItem value="recent" data-testid="sort-recent">Plus récents d&apos;abord</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <NutritionImporter onImported={load} />
          <Button
            variant="outline"
            onClick={() => navigate("/produits/traductions")}
            data-testid="open-translations-btn"
            title="Réviser les traductions FR · DE · IT côte-à-côte"
          >
            <Languages className="w-4 h-4 mr-2" /> Traductions
          </Button>
          <Dialog open={open} onOpenChange={async (next) => {
            // Flush auto-save si le dialog se ferme alors qu'une modif est en attente
            if (!next && editing && saveStatus === "dirty" && form.sku && form.name) {
              if (saveTimer.current) clearTimeout(saveTimer.current);
              try {
                setSaveStatus("saving");
                await api.put(`/products/${editing.id}`, buildPayload());
                setSaveStatus("saved");
                toast.success("Modifications enregistrées");
                await load();
              } catch {
                toast.error("Échec de l'enregistrement — vérifiez votre connexion");
              }
            }
            // Phase 87an — Re-scroll vers la ligne éditée à la fermeture du dialog
            const wasEditingId = !next && editing?.id ? editing.id : null;
            setOpen(next);
            if (wasEditingId) {
              setTimeout(() => {
                const row = document.querySelector(`[data-product-row-id="${wasEditingId}"]`);
                if (row) {
                  row.scrollIntoView({ behavior: "smooth", block: "center" });
                  row.classList.add("product-row-highlight");
                  setTimeout(() => row.classList.remove("product-row-highlight"), 2000);
                }
              }, 100);
            }
          }}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="create-product-btn">
                <Plus className="w-4 h-4 mr-2" /> Nouveau produit
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
            <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#E5E5E0]">
              <DialogTitle className="font-heading">{editing ? "Modifier le produit" : "Nouveau produit"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 px-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1.5 block">SKU *</Label>
                  <Input
                    value={form.sku}
                    onChange={(e) => setForm({ ...form, sku: e.target.value })}
                    placeholder="Ex: FAR-T55-25"
                    data-testid="form-sku"
                    className={skuCheck.status === "conflict" ? "border-[#C05C47] focus-visible:ring-[#C05C47]" : ""}
                  />
                  {/* Phase 87bf — Feedback live unicité SKU */}
                  <SkuNameCheckBadge
                    check={skuCheck}
                    field="SKU"
                    testid="sku-check-feedback"
                  />
                </div>
                <div>
                  <Label className="mb-1.5 block">Nom du produit *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Ex: Farine T55"
                    data-testid="form-name"
                    className={nameCheck.status === "conflict" ? "border-[#C05C47] focus-visible:ring-[#C05C47]" : ""}
                  />
                  {/* Phase 87bf — Feedback live unicité Nom */}
                  <SkuNameCheckBadge
                    check={nameCheck}
                    field="Nom"
                    testid="name-check-feedback"
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label className="mb-1.5 block">Catégorie</Label>
                  <Select value={form.category || "Général"} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="form-category">
                      <SelectValue placeholder="Choisir une catégorie…" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-[#A3A6A3] italic">
                          Aucune catégorie. Créez-en dans Paramètres → Catégories.
                        </div>
                      ) : (() => {
                        // Phase 87aw (24/06/2026) — Dédup par nom normalisé (lowercase, trim).
                        // Si la base contient des doublons (legacy ou casing différent),
                        // on ne garde que la 1ère occurrence pour ne pas afficher 2 fois
                        // la même entrée dans le menu déroulant.
                        const seen = new Set();
                        return categories.filter((c) => {
                          const k = (c.name || "").trim().toLowerCase();
                          if (!k || seen.has(k)) return false;
                          seen.add(k);
                          return true;
                        }).map((c) => (
                          <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                        ));
                      })()}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1.5 block">Unité</Label>
                  <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                    <SelectTrigger data-testid="form-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="g">g</SelectItem>
                      <SelectItem value="L">L</SelectItem>
                      <SelectItem value="mL">mL</SelectItem>
                      <SelectItem value="pcs">pcs</SelectItem>
                      <SelectItem value="carton">carton</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1.5 block">Seuil min.</Label>
                  <Input type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} data-testid="form-min-stock" />
                </div>
                <div>
                  <Label className="mb-1.5 block">DLC (jours)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.shelf_life_days}
                    onChange={(e) => setForm({ ...form, shelf_life_days: e.target.value })}
                    placeholder="Ex: 21"
                    data-testid="form-shelf-life"
                  />
                </div>
              </div>
              <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded-md p-3 flex items-center gap-3 flex-wrap" data-testid="copy-from-block">
                <Copy className="w-4 h-4 text-[#1F4E3D] shrink-0" />
                <Label className="text-xs uppercase tracking-wider text-[#575A57] mb-0">Copier depuis</Label>
                <Select
                  value=""
                  onValueChange={(srcId) => {
                    const src = products.find((p) => p.id === srcId);
                    if (!src) return;
                    setForm((f) => ({
                      ...f,
                      ingredients: src.ingredients || "",
                      allergens: [...(src.allergens || [])],
                      nutrition: { ...emptyNutrition, ...(src.nutrition || {}) },
                    }));
                    toast.success(`Données INCO copiées depuis « ${src.name} »`);
                  }}
                >
                  <SelectTrigger className="flex-1 min-w-[200px] max-w-[420px]" data-testid="copy-from-select">
                    <SelectValue placeholder="Choisir un produit source..." />
                  </SelectTrigger>
                  <SelectContent>
                    {products
                      .filter((p) => p.id !== editing?.id)
                      .filter((p) => p.ingredients || (p.allergens || []).length || (p.nutrition && Object.values(p.nutrition).some((v) => v != null && v !== "")))
                      .map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} {p.sku ? <span className="text-xs text-[#A3A6A3] font-mono">· {p.sku}</span> : null}
                        </SelectItem>
                      ))}
                    {products.filter((p) => p.id !== editing?.id && (p.ingredients || (p.allergens || []).length || (p.nutrition && Object.values(p.nutrition).some((v) => v != null && v !== "")))).length === 0 && (
                      <div className="px-3 py-2 text-xs text-[#A3A6A3]">Aucun produit n'a encore d'INCO renseignée.</div>
                    )}
                  </SelectContent>
                </Select>
                <span className="text-[11px] text-[#575A57]">
                  Recopie ingrédients, allergènes et nutrition. Vous pourrez ensuite ajuster.
                </span>
              </div>

              <div data-testid="prices-block" className="bg-white border border-[#1F4E3D]/20 rounded-md p-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <Label className="text-sm font-semibold text-[#1A1C1A]">Prix de vente (CHF, TTC — TVA 2,6% incluse)</Label>
                  {(() => {
                    // Phase 32b — coût "retenu" = manuel si saisi, sinon recette
                    const manualCost = form.cost_price !== "" && form.cost_price !== null ? Number(form.cost_price) : null;
                    const recipeCost = costBreakdown?.unit_cost_from_recipe;
                    const effectiveCost = (manualCost != null && manualCost > 0) ? manualCost : recipeCost;
                    if (!effectiveCost || effectiveCost <= 0) return null;
                    const source = (manualCost != null && manualCost > 0) ? "manuel" : "recette";
                    return (
                      <span
                        className="text-[11px] text-[#575A57] font-mono"
                        title={`Coût utilisé pour calculer les marges (${source})`}
                      >
                        Coût retenu : <strong className="text-[#1F4E3D]">{Number(effectiveCost).toFixed(2)} CHF</strong>
                        <span className="ml-1 text-[10px] text-[#A3A6A3]">({source})</span>
                      </span>
                    );
                  })()}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { key: "price_direct", label: "Vente directe", hint: "Distributeur auto / sur place" },
                    { key: "price_distributor", label: "Revendeur", hint: "Prix de cession B2B" },
                    { key: "price_online", label: "Internet", hint: "Boutique en ligne" },
                    { key: "price_traiteur", label: "Traiteur", hint: "Devis / offres traiteur" },
                  ].map((p) => {
                    const v = form[p.key];
                    // Phase 32b — utilise le coût "retenu" (manuel prioritaire, sinon recette)
                    const manualCost = form.cost_price !== "" && form.cost_price !== null ? Number(form.cost_price) : null;
                    const recipeCost = costBreakdown?.unit_cost_from_recipe;
                    const cost = (manualCost != null && manualCost > 0) ? manualCost : recipeCost;
                    const price = Number(v);
                    const showMargin = !!cost && cost > 0 && !!v && price > cost;
                    const marginChf = showMargin ? (price - cost) : null;
                    const marginPct = showMargin ? Math.round(((price - cost) / price) * 100) : null;
                    return (
                      <div key={p.key}>
                        <Label className="mb-1 block text-xs text-[#575A57]">{p.label}</Label>
                        <div className="relative">
                          <NumericInput
                            value={v ?? ""}
                            onChange={(e) => setForm({ ...form, [p.key]: e.target.value })}
                            className="pr-12 font-mono"
                            placeholder="—"
                            data-testid={`form-${p.key}`}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#A3A6A3] font-mono">CHF</span>
                        </div>
                        <div className="text-[10px] text-[#A3A6A3] mt-1 flex items-center justify-between gap-2">
                          <span>{p.hint}</span>
                          {marginChf != null && (() => {
                            const isLow = marginPct < minMarginPct;
                            const color = isLow
                              ? "text-[#C05C47]"
                              : marginPct >= 50
                              ? "text-[#1F4E3D]"
                              : "text-[#8C6B2F]";
                            return (
                              <span
                                className={`font-mono font-semibold whitespace-nowrap flex items-center gap-1 ${color}`}
                                title={
                                  isLow
                                    ? `Marge sous le seuil minimum (${minMarginPct}%) — à surveiller`
                                    : `Marge ${marginPct}%`
                                }
                                data-testid={`margin-${p.key}${isLow ? "-low" : ""}`}
                              >
                                {isLow && <AlertTriangle className="w-3 h-3 flex-shrink-0" />}
                                +{marginChf.toFixed(2)} CHF · +{marginPct}%
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3">
                  <div className="col-span-1">
                    <Label className="mb-1 block text-xs text-[#575A57]">Coût de revient (manuel)</Label>
                    <div className="relative">
                      <NumericInput
                        value={form.cost_price ?? ""}
                        onChange={(e) => setForm({ ...form, cost_price: e.target.value })}
                        className="pr-12 font-mono"
                        placeholder="—"
                        data-testid="form-cost-price"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#A3A6A3] font-mono">CHF</span>
                    </div>
                    {/* Phase 32b — coût calculé recette + bouton pour reprendre la valeur
                        Phase 60 — total inclut maintenant le packaging */}
                    {costBreakdown && (costBreakdown.total_unit_cost != null || costBreakdown.unit_cost_from_recipe != null) && (
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, cost_price: Number(costBreakdown.total_unit_cost ?? costBreakdown.unit_cost_from_recipe).toFixed(2) })}
                        className="mt-1.5 text-[10px] text-[#1F4E3D] hover:underline flex items-center gap-1 group"
                        data-testid="use-recipe-cost"
                        title="Cliquez pour reprendre cette valeur (ingrédients + emballage) dans le champ manuel"
                      >
                        <span className="opacity-80">💡 Recette + emballage :</span>
                        <strong className="font-mono">
                          {Number(costBreakdown.total_unit_cost ?? costBreakdown.unit_cost_from_recipe ?? 0).toFixed(2)} CHF
                        </strong>
                        {costBreakdown.packaging?.total_cost_chf > 0 && (
                          <span className="text-[9px] text-[#575A57] font-mono">
                            ({(costBreakdown.unit_cost_from_recipe ?? 0).toFixed(2)} + {costBreakdown.packaging.total_cost_chf.toFixed(3)})
                          </span>
                        )}
                        {costBreakdown.completeness != null && costBreakdown.completeness < 0.99 && (
                          <span className="text-[9px] text-[#8C6B2F]">({Math.round(costBreakdown.completeness * 100)} % ingrédients)</span>
                        )}
                        <span className="ml-1 text-[9px] opacity-0 group-hover:opacity-100 transition-opacity">→ utiliser</span>
                      </button>
                    )}
                  </div>
                  <div className="col-span-2 text-[11px] text-[#575A57] flex items-end pb-1">
                    {costBreakdown?.unit_cost_from_recipe != null
                      ? <>Le coût est calculé automatiquement à partir des ingrédients valorisés dans la <Link to={editing ? `/produits/${editing.id}/fiche` : "#"} className="underline text-[#1F4E3D]">fiche technique</Link>. Saisissez une valeur manuelle uniquement si vous voulez la forcer (elle prendra alors le dessus).</>
                      : <>Saisissez ici le coût matières (HT) si vous n'avez pas renseigné les prix d'achat de chaque ingrédient dans la <Link to={editing ? `/produits/${editing.id}/fiche` : "#"} className="underline text-[#1F4E3D]">fiche technique</Link>. Sinon, le coût sera automatiquement calculé à partir de la recette.</>}
                  </div>
                </div>
              </div>

              {/* Boutique en ligne — composant extrait (iter22 modularisation) */}
              <ProductShopBlock form={form} setForm={setForm} productId={editing?.id} />

              {/* Phase 60 — Consommables emballage (invisible cuisine, ajoute au prix de revient) */}
              <ProductPackagingEditor
                packaging={form.packaging}
                onChange={(next) => setForm({ ...form, packaging: next })}
                breakdownPackaging={costBreakdown?.packaging}
              />

              <div>
                <Label className="mb-2 block">Allergènes</Label>
                <div className="flex flex-wrap gap-2">
                  {ALLERGENS.map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => toggleAllergen(a)}
                      data-testid={`allergen-${a}`}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        form.allergens.includes(a) ? "bg-[#1F4E3D] text-white border-[#1F4E3D]" : "bg-white text-[#575A57] border-[#E5E5E0] hover:bg-[#F0F0EB]"
                      }`}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block">Liste d'ingrédients (INCO)</Label>
                <Textarea
                  value={form.ingredients}
                  onChange={(e) => setForm({ ...form, ingredients: e.target.value })}
                  rows={3}
                  placeholder="Riz, poulet (30%), lait de coco, pâte de curry rouge (piment, ail, citronnelle), sucre, sel…"
                  data-testid="form-ingredients"
                />
                <p className="text-xs text-[#575A57] mt-1.5">
                  Affichée sur l'étiquette et la fiche technique. Conforme au Règlement INCO (UE n°1169/2011) — mettez les allergènes en MAJUSCULES et ajoutez le <span className="font-mono">(%)</span> des ingrédients principaux (viande, poisson, etc.).
                </p>
              </div>
              <div>
                <Label className="mb-2 block">Valeurs nutritionnelles (pour 100 g / 100 mL)</Label>
                <div className="grid grid-cols-2 gap-3 bg-[#F9F9F6] border border-[#E5E5E0] rounded-md p-3">
                  {NUTRITION_FIELDS.map((f) => (
                    <div key={f.key}>
                      <Label className="mb-1 block text-xs text-[#575A57]">{f.label}</Label>
                      <div className="relative">
                        <NumericInput
                          value={form.nutrition?.[f.key] ?? ""}
                          onChange={(e) => setForm({ ...form, nutrition: { ...form.nutrition, [f.key]: e.target.value } })}
                          placeholder="—"
                          className="pr-12"
                          data-testid={`form-nutrition-${f.key}`}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#A3A6A3] font-mono pointer-events-none">{f.unit}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#575A57] mt-1.5">
                  Tous les champs sont optionnels. Si au moins une valeur est saisie, le tableau apparaît sur l'étiquette.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="mb-1.5 block">Poids net par barquette</Label>
                  <div className="flex gap-2">
                    <NumericInput
                      value={form.net_weight}
                      onChange={(e) => setForm({ ...form, net_weight: e.target.value })}
                      placeholder="550"
                      data-testid="form-net-weight"
                    />
                    <Select value={form.net_weight_unit} onValueChange={(v) => setForm({ ...form, net_weight_unit: v })}>
                      <SelectTrigger className="w-24" data-testid="form-net-weight-unit"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="g">g</SelectItem>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="mL">mL</SelectItem>
                        <SelectItem value="L">L</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Input
                    value={form.net_weight_detail}
                    onChange={(e) => setForm({ ...form, net_weight_detail: e.target.value })}
                    placeholder="Ex: dont 300 g de riz"
                    className="mt-2"
                    data-testid="form-net-weight-detail"
                  />
                  <p className="text-xs text-[#575A57] mt-1.5">Affiché sur l'étiquette à côté du nom du produit.</p>
                </div>
                <div>
                  <Label className="mb-1.5 block">Mode de conservation</Label>
                  <Textarea
                    value={form.storage_instructions}
                    onChange={(e) => setForm({ ...form, storage_instructions: e.target.value })}
                    rows={2}
                    placeholder="Ex: ≤ 5°C"
                    data-testid="form-storage"
                  />
                </div>
                <div>
                  <Label className="mb-1.5 block">Instructions de réchauffage</Label>
                  <Textarea
                    value={form.reheating_instructions}
                    onChange={(e) => setForm({ ...form, reheating_instructions: e.target.value })}
                    rows={2}
                    placeholder="Ex: Réchauffer 3 min au micro-ondes 800 W"
                    data-testid="form-reheating"
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label>Code EAN-13 (12 chiffres — 13e auto-calculé)</Label>
                  {/* Phase 87aj — Suggestion visible aussi en édition tant que le champ est vide */}
                  {!form.ean13_base && eanSuggestion?.configured && eanSuggestion.next_full && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, ean13_base: eanSuggestion.next_full })}
                      className="text-[11px] text-[#1F4E3D] hover:underline font-mono"
                      data-testid="use-suggested-ean"
                      title={`Cliquez pour utiliser ${eanSuggestion.next_full}`}
                    >
                      Prochain libre : <strong>{eanSuggestion.next_full}</strong> ↗
                    </button>
                  )}
                </div>
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={12}
                  value={form.ean13_base}
                  onChange={(e) => setForm({ ...form, ean13_base: e.target.value.replace(/\D/g, "").slice(0, 12) })}
                  placeholder={eanSuggestion?.next_full ? `Ex: ${eanSuggestion.next_full}` : "Ex: 760100012345"}
                  className="font-mono"
                  data-testid="form-ean13-base"
                />
                {/* Phase 87ai — Affichage EAN13 final + bouton "Copier pour Nayax" */}
                {form.ean13_base.length === 12 && (
                  <div className="mt-2 p-3 bg-[#FBF4E9] border border-[#E8D9B8] rounded flex items-center justify-between gap-2" data-testid="ean13-final-block">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-[#8C6B2F] font-semibold">EAN13 final (à recopier dans Nayax)</div>
                      <div className="font-mono text-lg font-bold text-[#1A1C1A] tracking-wider" data-testid="ean13-final-value">
                        {form.ean13_base}<span className="text-[#8C6B2F]">{ean13CheckDigit(form.ean13_base)}</span>
                      </div>
                      <div className="text-[10px] text-[#575A57] mt-0.5">
                        Code immuable utilisé par les caisses revendeurs · 1 produit = 1 EAN13 unique
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FDF3E0] shrink-0"
                      onClick={() => {
                        const full = `${form.ean13_base}${ean13CheckDigit(form.ean13_base)}`;
                        navigator.clipboard.writeText(full);
                        toast.success(`EAN13 ${full} copié — collez-le dans la base Nayax`);
                      }}
                      data-testid="ean13-copy-for-nayax"
                    >
                      📋 Copier pour Nayax
                    </Button>
                  </div>
                )}
                <p className="text-xs text-[#575A57] mt-1.5">
                  {form.ean13_base.length === 12 ? (
                    <>Chiffre de contrôle auto : <span className="font-bold">{ean13CheckDigit(form.ean13_base)}</span></>
                  ) : form.ean13_base.length > 0 ? (
                    <>{form.ean13_base.length} / 12 chiffres saisis — complétez pour voir le code final.</>
                  ) : (
                    <>Laisser vide pour attribuer automatiquement le prochain EAN13 GS1 disponible à l&apos;enregistrement.</>
                  )}
                </p>
                {/* Phase 87ai — Avertissement à l'édition d'un EAN13 déjà attribué */}
                {editing && (form.ean13_base?.length === 12) && (
                  <div className="mt-2 p-2 bg-[#FCEEEA] border border-[#E8C1B4] rounded text-[11px] text-[#8B1A1A] leading-snug" data-testid="ean13-edit-warning">
                    <strong>⚠ EAN13 immuable</strong> — Modifier ce code cassera la synchro avec les caisses revendeurs et la base Nayax.
                    Si vous changez la valeur, pensez à <strong>recopier le nouveau code dans Nayax</strong> immédiatement.
                  </div>
                )}
                {!editing && eanSuggestion && (
                  <p className="text-[10px] text-[#A3A6A3] mt-1" data-testid="ean-capacity-hint">
                    {eanSuggestion.hint}
                  </p>
                )}
              </div>
              {/* Phase 87aw (24/06/2026) — Alias Nayax : noms alternatifs reconnus par
                  la sync (utile pour anciens noms SCAV, vieux Plans, variations
                  d'écriture). Le matching cherche d'abord par EAN13, puis par nom,
                  puis dans cette liste d'aliases. */}
              <div>
                <Label className="mb-1.5 block flex items-center gap-2">
                  Alias Nayax (noms alternatifs)
                  <span className="text-[10px] uppercase tracking-wider text-[#A3A6A3] font-normal">facultatif</span>
                </Label>
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid="nayax-aliases-list">
                  {(form.nayax_aliases || []).length === 0 ? (
                    <span className="text-xs text-[#A3A6A3] italic">Aucun alias — le matching utilisera uniquement le nom principal + EAN13</span>
                  ) : (
                    form.nayax_aliases.map((a, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-[#FBF4E9] border border-[#E8D9B8] rounded text-xs"
                        data-testid={`nayax-alias-${i}`}
                      >
                        <span className="font-mono">{a}</span>
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, nayax_aliases: form.nayax_aliases.filter((_, j) => j !== i) })}
                          className="text-[#8C6B2F] hover:text-[#8B1A1A] font-bold leading-none"
                          aria-label={`Retirer l'alias ${a}`}
                          data-testid={`nayax-alias-remove-${i}`}
                        >
                          ×
                        </button>
                      </span>
                    ))
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Ex: Bœuf cajou, Boeuf au noix de cajou…"
                    value={form._aliasDraft || ""}
                    onChange={(e) => setForm({ ...form, _aliasDraft: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const v = (form._aliasDraft || "").trim();
                        if (v && !(form.nayax_aliases || []).includes(v)) {
                          setForm({ ...form, nayax_aliases: [...(form.nayax_aliases || []), v], _aliasDraft: "" });
                        }
                      }
                    }}
                    data-testid="nayax-alias-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const v = (form._aliasDraft || "").trim();
                      if (v && !(form.nayax_aliases || []).includes(v)) {
                        setForm({ ...form, nayax_aliases: [...(form.nayax_aliases || []), v], _aliasDraft: "" });
                      }
                    }}
                    data-testid="nayax-alias-add"
                  >
                    Ajouter
                  </Button>
                </div>
                <p className="text-[11px] text-[#575A57] mt-1.5">
                  Ajoutez les anciens noms / variantes d&apos;orthographe utilisés dans Nayax (MoMa) pour que la sync les reconnaisse même si le nom principal a changé (ex: renommages SCAV). Le matching reste tolérant aux articles (au/aux/de/des) et aux accents.
                </p>
              </div>
              {/* Phase 98al — Champ actif : NayaxProductID nécessaire pour push MOMA (sync AgroManage → Nayax). */}
              <div>
                <Label className="mb-1.5 block">
                  ID Produit Nayax <span className="text-[10px] font-normal text-[#575A57]">(NayaxProductID)</span>
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="ex: 338657544808514"
                  value={form.nayax_product_id || ""}
                  onChange={(e) => {
                    const digitsOnly = e.target.value.replace(/[^0-9]/g, "");
                    setForm({ ...form, nayax_product_id: digitsOnly });
                  }}
                  className="font-mono"
                  data-testid="form-nayax-product-id"
                />
                <p className="text-[11px] text-[#575A57] mt-1.5">
                  <strong>Utilisé activement</strong> pour la synchronisation AgroManage → Nayax MOMA (push
                  restock au verrouillage de tournée et rattrapage manuel). Copie le champ
                  <strong> « ID Produit Nayax »</strong> depuis Nayax Core → fiche produit (attribué
                  automatiquement à la création, généralement 13–15 chiffres). <span className="text-[#8C6B2F]">Ne
                  pas confondre avec « Ref Produit » ou « EAN »</span>.
                </p>
              </div>
              <div>
                <Label className="mb-1.5 block">Description (interne)</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="form-description" />
              </div>
            </div>
            <DialogFooter className="sm:justify-between">
              <div className="text-xs flex items-center min-h-[20px]" data-testid="product-save-status">
                {editing && saveStatus === "saving" && (
                  <span className="text-[#575A57] flex items-center gap-1.5">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Enregistrement…
                  </span>
                )}
                {editing && saveStatus === "saved" && (
                  <span className="text-[#1F4E3D] flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5" /> Enregistré automatiquement
                  </span>
                )}
                {editing && saveStatus === "dirty" && (
                  <span className="text-[#8C6B2F] flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#8C6B2F]"></span> Modifications en cours…
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>{editing ? "Fermer" : "Annuler"}</Button>
                {!editing && (
                  <Button
                    onClick={save}
                    className="bg-[#1F4E3D] hover:bg-[#173B2E]"
                    data-testid="save-product-btn"
                    disabled={skuCheck.status === "conflict" || nameCheck.status === "conflict" || skuCheck.status === "checking" || nameCheck.status === "checking"}
                  >
                    Créer
                  </Button>
                )}
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filtre de visibilité catalogue */}
      <div className="flex items-center gap-2 flex-wrap text-sm" data-testid="catalog-filter-chips">
        <span className="text-xs uppercase tracking-wider text-[#575A57] font-semibold">Filtrer&nbsp;:</span>
        {[
          { id: "all", label: `Tous (${counts.total})`, color: "bg-[#F4F4F0] text-[#3D2914] border-[#E5E5E0]" },
          { id: "noean", label: `🏷️ Manque EAN (${counts.noean})`, color: "bg-[#FCEEEA] text-[#8B1A1A] border-[#E8C1B4]" },
          { id: "in", label: `✓ Complets (${counts.complete})`, color: "bg-[#E8F1EA] text-[#1F4E3D] border-[#B9D6BD]" },
          { id: "nopackaging", label: `⚠ Sans imagette (${counts.nopackaging})`, color: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" },
          { id: "nophoto", label: `📷 Sans photo principale (${counts.nophoto})`, color: "bg-[#FBEEDB] text-[#A04E2C] border-[#E8C7BD]" },
          { id: "hidden", label: `🚫 Masqués (${counts.hidden})`, color: "bg-[#FBE5E0] text-[#A04E2C] border-[#E8C7BD]" },
        ].map((chip) => (
          <button
            key={chip.id}
            onClick={() => setCatalogFilter(chip.id)}
            className={`px-3 py-1 rounded-full border text-xs font-medium transition ${catalogFilter === chip.id ? `${chip.color} ring-2 ring-offset-1 ring-[#8C6B2F]/30` : "bg-white text-[#575A57] border-[#E5E5E0] hover:bg-[#FAFAF8]"}`}
            data-testid={`catalog-filter-${chip.id}`}
          >
            {chip.label}
          </button>
        ))}
        {/* Phase 87bf → 101di — Filtre archivés en 3-state segmenté
            (Actifs | Tous | Inactifs). Le mode « Inactifs » retrouve en 1 clic
            les produits archivés à réactiver sans devoir parcourir la liste. */}
        <div
          className="flex items-center gap-0.5 ml-auto rounded-md border border-[#E5E5E0] bg-white p-0.5"
          data-testid="archive-filter-segmented"
          role="radiogroup"
          aria-label="Filtre statut produits"
        >
          {[
            { id: "active",   label: "Actifs",   color: "bg-[#1F4E3D] text-white",     textColor: "text-[#1F4E3D]" },
            { id: "all",      label: "Tous",     color: "bg-[#575A57] text-white",     textColor: "text-[#575A57]" },
            { id: "inactive", label: "Inactifs", color: "bg-[#8C6B2F] text-white",     textColor: "text-[#8C6B2F]" },
          ].map((opt) => {
            const active = archiveFilter === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setArchiveFilter(opt.id)}
                className={`px-2.5 py-1 rounded text-[11px] font-medium uppercase tracking-wider transition inline-flex items-center gap-1 ${active ? opt.color : `bg-transparent hover:bg-[#F5F5F1] ${opt.textColor}`}`}
                data-testid={`archive-filter-${opt.id}`}
              >
                {opt.id === "inactive" && <Archive className="w-3 h-3" />}
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Phase 98ac — Bandeau statut Nayax : combien de produits sont mappés */}
      {(() => {
        const visible = sorted.filter((p) => !p.archived);
        const missing = visible.filter((p) => !p.nayax_product_id);
        if (visible.length === 0) return null;
        const okCount = visible.length - missing.length;
        const allOk = missing.length === 0;
        return (
          <div
            className={`p-2.5 rounded border text-xs flex items-center gap-2 flex-wrap ${
              allOk
                ? "bg-[#E8F0E5] border-[#C8D9C0] text-[#5A7A4F]"
                : "bg-[#FBF4E9] border-[#E8D3A8] text-[#8C6B2F]"
            }`}
            data-testid="nayax-mapping-banner"
          >
            <span>📡</span>
            <strong>Sync Nayax MOMA :</strong>
            <span>
              {okCount}/{visible.length} produit(s) mappé(s)
              {!allOk && (
                <>
                  {" · "}
                  <strong>{missing.length} à configurer</strong>
                  {" — "}
                  <span className="italic">
                    ouvrez la fiche du produit → onglet Nayax → renseignez le NayaxProductID
                  </span>
                </>
              )}
            </span>
          </div>
        );
      })()}

      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F4F0] hover:bg-[#F4F4F0]">
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">SKU</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Nom</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Catégorie</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Catalogue</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Allergènes</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Prix · Marge</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Seuil min.</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="products-table-body">
            {sorted.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center text-[#575A57] py-10">Aucun produit.</TableCell></TableRow>
            ) : sorted.map((p) => {
              const hasPhoto = !!(p.photo_url && String(p.photo_url).trim());
              const hasPackaging = !!(p.photo_packaging_url && String(p.photo_packaging_url).trim());
              const isVisible = p.online_visible !== false;
              const inCatalog = isVisible && hasPhoto;
              return (
              <TableRow
                key={p.id}
                data-testid={`product-row-${p.sku}`}
                data-product-row-id={p.id}
                className={p.archived ? "opacity-50 bg-[#F4F4F0]/40" : ""}
              >
                <TableCell className="font-mono text-sm">
                  {(() => {
                    const dup = eanDuplicateMap.get(p.id);
                    const hasEan = !!(p.ean13_base && p.ean13_base.trim().length === 12);
                    // Phase 98ac — Badge Nayax : rouge si nayax_product_id manquant/invalide
                    const npid = p.nayax_product_id;
                    const nayaxMissing = npid === null || npid === undefined || npid === "" || npid === 0;
                    return (
                      <span className="inline-flex items-center gap-1.5 flex-wrap">
                        {dup && (
                          <AlertTriangle
                            className="w-4 h-4 text-[#C05C47] shrink-0"
                            title={`EAN13 ${dup.base} en doublon avec : ${dup.others.map((o) => o.sku + " (" + o.name + ")").join(", ")}`}
                            data-testid={`ean13-duplicate-warning-${p.sku}`}
                          />
                        )}
                        <span>{p.sku}</span>
                        {!hasEan && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#FCEEEA] text-[#8B1A1A] border border-[#E8C1B4] font-sans"
                            title="EAN13 manquant — assignez un code avant impression des étiquettes"
                            data-testid={`missing-ean-${p.sku}`}
                          >
                            🏷️ manque EAN
                          </span>
                        )}
                        {nayaxMissing ? (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#FCEEEA] text-[#8B1A1A] border border-[#E8C1B4] font-sans"
                            title="NayaxProductID manquant — le sync automatique vers MOMA ne fonctionnera pas pour ce produit. Ouvrez la fiche produit et renseignez le champ NayaxProductID (entier numérique fourni par Nayax Core)."
                            data-testid={`nayax-missing-${p.sku}`}
                          >
                            📡 no Nayax
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#E8F0E5] text-[#5A7A4F] border border-[#C8D9C0] font-sans font-mono"
                            title={`NayaxProductID = ${npid} — sync MOMA activé pour ce produit.`}
                            data-testid={`nayax-ok-${p.sku}`}
                          >
                            📡 {npid}
                          </span>
                        )}
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell className="font-medium">
                  {p.name}
                  {p.archived && (
                    <Badge variant="outline" className="ml-2 text-[10px] bg-[#F4F4F0] text-[#575A57] border-[#C8C8C0]" data-testid={`archived-badge-${p.sku}`}>
                      <Archive className="w-2.5 h-2.5 mr-0.5" /> Archivé
                    </Badge>
                  )}
                </TableCell>
                <TableCell><Badge variant="secondary">{p.category}</Badge></TableCell>
                <TableCell data-testid={`catalog-status-${p.sku}`}>
                  {!isVisible ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#FBE5E0] text-[#A04E2C] border border-[#E8C7BD]" title="Masqué de la boutique → exclu des catalogues">
                      <EyeOff className="w-3 h-3" /> Masqué
                    </span>
                  ) : !hasPhoto ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#FBEEDB] text-[#A04E2C] border border-[#E8C7BD]" title="Pas de photo principale → exclu des catalogues imprimables. Ajoutez la photo du plat dressé.">
                      <ImageIcon className="w-3 h-3" /> Sans photo principale
                    </span>
                  ) : !hasPackaging ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#FBF4E9] text-[#8C6B2F] border border-[#E8D3A8]" title="Inclus dans le catalogue, mais imagette barquette manquante (utilisée sur la page produit boutique)">
                      <CheckCircle2 className="w-3 h-3" /> Inclus
                      <span className="text-[10px] opacity-75">· ⚠ sans imagette</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#E8F1EA] text-[#1F4E3D] border border-[#B9D6BD]" title="Photo principale + imagette barquette présentes">
                      <CheckCircle2 className="w-3 h-3" /> Complet
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(p.allergens || []).slice(0, 3).map((a) => (
                      <Badge key={a} variant="outline" className="text-xs bg-[#FBF4E9] border-[#E8D3A8] text-[#8C6B2F]">{a}</Badge>
                    ))}
                    {(p.allergens || []).length > 3 && <span className="text-xs text-[#575A57]">+{p.allergens.length - 3}</span>}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <PriceMarginCell product={p} minMarginPct={minMarginPct} />
                </TableCell>
                <TableCell className="text-right font-mono">{p.min_stock} {p.unit}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end items-center gap-1">
                    {/* Phase 101dh (Feb 2026) — Switch actif/désactivé visible
                        en permanence dans la ligne (avant les autres actions).
                        Phase 101ef (Feb 2026) — Sur produit inactif, remplace
                        le toggle OFF (visuellement grisé et perçu comme
                        disabled) par un vrai bouton "Réactiver" explicite. */}
                    {p.archived ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => reactivate(p)}
                        data-testid={`reactivate-${p.sku}`}
                        className="h-7 text-xs border-[#1F4E3D] text-[#1F4E3D] hover:bg-[#EAF4EE] mr-2"
                        title="Remettre ce produit dans le catalogue actif"
                      >
                        <ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Réactiver
                      </Button>
                    ) : (
                      <div className="flex items-center gap-1.5 mr-2" title="Produit actif — cliquez pour désactiver">
                        <Switch
                          checked={true}
                          onCheckedChange={() => remove(p)}
                          data-testid={`toggle-active-${p.sku}`}
                        />
                        <span className="text-[10px] uppercase tracking-wider font-medium w-[52px] text-[#1F4E3D]">
                          Actif
                        </span>
                      </div>
                    )}
                    <Link to={`/produits/${p.id}/fiche`}>
                      <Button variant="ghost" size="icon" title="Fiche technique" data-testid={`tech-sheet-${p.sku}`}>
                        <FileText className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-product-${p.sku}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// Phase 87bf — Badge feedback live unicité SKU / Nom dans le dialog produit
function SkuNameCheckBadge({ check, field, testid }) {
  if (check.status === "idle") return null;
  if (check.status === "checking") {
    return (
      <div className="text-[11px] text-[#A3A6A3] mt-1 flex items-center gap-1" data-testid={testid}>
        <Loader2 className="w-3 h-3 animate-spin" /> Vérification…
      </div>
    );
  }
  if (check.status === "ok") {
    return (
      <div className="text-[11px] text-[#2D6A4F] mt-1 flex items-center gap-1" data-testid={testid}>
        <CheckCircle2 className="w-3 h-3" /> {field} disponible
      </div>
    );
  }
  if (check.status === "conflict") {
    const c = check.conflict;
    return (
      <div className="text-[11px] text-[#C05C47] mt-1 flex items-start gap-1 leading-snug" data-testid={testid}>
        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
        <span>
          {field} déjà utilisé par <strong>« {c?.name} »</strong> (SKU {c?.sku})
          {" — Nayax refuse les doublons."}
        </span>
      </div>
    );
  }
  return null;
}
